import React from 'react';
import clsx from 'clsx';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * The visual style variant of the button
   */
  variant?: 'primary' | 'secondary' | 'tertiary' | 'success' | 'danger' | 'warning' | 'info' | 'light' | 'dark';
  
  /**
   * The size of the button
   */
  size?: 'xs' | 'sm' | 'base' | 'lg';

  /**
   * Renders the outline variant
   */
  outline?: boolean;
  
  /**
   * Whether the button should take the full width of its container
   */
  fullWidth?: boolean;
  
  /**
   * Whether the button is in a loading state
   */
  loading?: boolean;
  
  /**
   * Icon to display before the button text
   */
  startIcon?: React.ReactNode;
  
  /**
   * Icon to display after the button text
   */
  endIcon?: React.ReactNode;
  
  /**
   * The content of the button
   */
  children: React.ReactNode;
}

/**
 * Button component with consistent styling and behavior
 */
export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'base',
      outline,
      fullWidth = false,
      loading = false,
      startIcon,
      endIcon,
      disabled,
      className,
      children,
      ...props
    },
    ref
  ) => {
    return (
      <button
        ref={ref}
        className={clsx(
          'prometheus-button',
          `prometheus-button--${variant}${outline ? '-outline' : ''}`,
          `prometheus-button--${size}`,
          {
            'prometheus-button--full-width': fullWidth,
            'prometheus-button--loading': loading,
            'prometheus-button--disabled': disabled || loading,
          },
          className
        )}
        disabled={disabled || loading}
        {...props}
      >
        {loading && (
          <span className="prometheus-button__spinner" aria-hidden="true">
            <svg className="prometheus-button__spinner-icon" viewBox="0 0 24 24">
              <circle
                className="prometheus-button__spinner-circle"
                cx="12"
                cy="12"
                r="10"
                fill="none"
                strokeWidth="2"
              />
            </svg>
          </span>
        )}
        
        {!loading && startIcon && (
          <span className="prometheus-button__start-icon" aria-hidden="true">
            {startIcon}
          </span>
        )}
        
        <span className="prometheus-button__content">
          {children}
        </span>
        
        {!loading && endIcon && (
          <span className="prometheus-button__end-icon" aria-hidden="true">
            {endIcon}
          </span>
        )}
      </button>
    );
  }
);

Button.displayName = 'Button';

export default Button;